import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    MdArrowBack,
    MdBusiness,
    MdDescription,
    MdEdit,
    MdPerson,
    MdReceiptLong,
} from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { getLocationById } from '../../Services/LocationService';
import ViewEmptyState from '../../Components/Common/ViewEmptyState';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { APPRoutes } from '../../Utils/Route';

const displayValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
    return String(value);
};

const formatDate = (value) => {
    if (!value) return '-';
    const dateValue = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value;
    const date = new Date(dateValue);
    return Number.isNaN(date.getTime()) ? displayValue(value) : date.toLocaleDateString();
};

function FieldCard({ label, value, icon: Icon, format = displayValue }) {
    return (
        <div className="col-12 col-md-6">
            <div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
                {Icon && (
                    <span
                        className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border flex-shrink-0"
                        style={{ width: 40, height: 40 }}
                    >
                        <Icon size={18} className="hc-text-primary" />
                    </span>
                )}
                <div className="text-start">
                    <div className="small text-muted">{label}</div>
                    <div className="fw-semibold text-dark text-break">{format(value)}</div>
                </div>
            </div>
        </div>
    );
}

function DetailSection({ title, icon: Icon, children }) {
    return (
        <section className="p-4 p-lg-5 hc-surface hc-card-primary h-100">
            <h5 className="fw-bold mb-4 d-flex align-items-center gap-2 hc-text-primary">
                {Icon && <Icon size={22} />}
                {title}
            </h5>
            <div className="row g-3">{children}</div>
        </section>
    );
}

function AddressFields({ address, prefix }) {
    return (
        <>
            <FieldCard label={`${prefix} Address`} value={address?.addressLine1} />
            <FieldCard label={`${prefix} City`} value={address?.city} />
            <FieldCard label={`${prefix} State`} value={address?.state} />
            <FieldCard label={`${prefix} Postal Code`} value={address?.postalCode} />
        </>
    );
}

function ProviderFields({ title, provider }) {
    return (
        <div className="col-12">
            <h6 className="fw-semibold text-dark mt-2 mb-3">{title}</h6>
            <div className="row g-3">
                <FieldCard label="First Name" value={provider?.name?.person?.firstName} />
                <FieldCard label="Last Name" value={provider?.name?.person?.lastName} />
                <FieldCard label="NPI" value={provider?.identifiers?.npi} />
            </div>
        </div>
    );
}

export function ClaimSubmissionView() {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const { token, permissions, decodedToken } = useAuth();
    const permissionCodes = React.useMemo(
        () => resolveUserPermissionCodes({ permissions, decodedToken }),
        [permissions, decodedToken],
    );
    const canEdit = permissionHas(permissionCodes, PermissionCodes.CLAIM_SUBMISSION_EDIT, decodedToken);
    const canViewList = permissionHas(permissionCodes, PermissionCodes.CLAIM_SUBMISSION_LIST, decodedToken);
    const [claimSubmission, setClaimSubmission] = React.useState(location.state?.claimSubmission ?? null);
    const [loading, setLoading] = React.useState(!location.state?.claimSubmission);

    React.useEffect(() => {
        if (!id || !token) return undefined;

        let isCurrent = true;
        const loadClaimSubmission = async () => {
            try {
                setLoading(true);
                const response = await getLocationById(id, token);
                if (response?.is_success) {
                    const record = response?.data?.data ?? response?.data ?? response;
                    if (isCurrent) setClaimSubmission(record);
                } else {
                    throw new Error(response?.message || 'Failed to load claim submission.');
                }
            } catch (error) {
                console.error('Failed to load claim submission:', error);
                toast.error(error.message || 'Failed to load claim submission.');
            } finally {
                if (isCurrent) setLoading(false);
            }
        };

        loadClaimSubmission();
        return () => {
            isCurrent = false;
        };
    }, [id, token]);

    if (loading && !claimSubmission) {
        return (
            <div className="container-fluid py-4">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 240 }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading claim submission...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!claimSubmission) {
        return (
            <ViewEmptyState
                title="Claim Submission Not Found"
                description="This claim submission could not be loaded."
                icon={MdDescription}
                canViewList={canViewList}
                backRoute={APPRoutes.CLAIM_SUBMISSION}
                backLabel="Back to Claim Submissions"
            />
        );
    }

    const authorization = claimSubmission.authorization || {};
    const billing = claimSubmission.billing || {};
    const billingProvider = billing.billingProvider || {};
    const insured = claimSubmission.insured || {};
    const patient = claimSubmission.patient || {};
    const payer = claimSubmission.payer || {};
    const encounter = claimSubmission.encounter || {};
    const clinicalDates = encounter.clinicalDates || {};
    const referenceNumbers = encounter.referenceNumbers || {};
    const submitter = claimSubmission.submitter || {};
    const insuredName = [
        insured.name?.person?.firstName,
        insured.name?.person?.lastName,
    ].filter(Boolean).join(' ') || '-';
    const patientName = [
        patient.name?.person?.firstName,
        patient.name?.person?.lastName,
    ].filter(Boolean).join(' ') || '-';
    const serviceLines = Array.isArray(claimSubmission.serviceLines) ? claimSubmission.serviceLines : [];
    const otherInsured = Array.isArray(claimSubmission.otherInsured) ? claimSubmission.otherInsured : [];
    const attachments = Array.isArray(encounter.attachments) ? encounter.attachments : [];
    const claimCodes = Array.isArray(encounter.claimCodes) ? encounter.claimCodes : [];
    const heroTitle = insuredName !== '-' ? insuredName : billingProvider.name?.organization || 'Claim Submission';

    return (
        <div className="container-fluid py-4">
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                {canViewList && (
                    <button
                        type="button"
                        className="btn btn-light fw-semibold border shadow-sm d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                        onClick={() => navigate(APPRoutes.CLAIM_SUBMISSION)}
                        style={{ minHeight: 40 }}
                    >
                        <MdArrowBack size={18} />
                        <span>Back to Claim Submissions</span>
                    </button>
                )}
                {canEdit && (
                    <button
                        type="button"
                        className="btn hc-bg-primary text-white d-inline-flex align-items-center gap-2"
                        onClick={() => navigate(APPRoutes.CLAIM_SUBMISSION_EDIT.replace(':id', id))}
                    >
                        <MdEdit size={18} /> Edit Claim Submission
                    </button>
                )}
            </div>

            <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
                <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
                <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />
                <div className="row g-4 align-items-center position-relative">
                    <div className="col-lg-8">
                        <span
                            className="px-3 py-2 rounded-pill fw-semibold d-inline-block mb-3"
                            style={{
                                fontSize: '0.82rem',
                                backgroundColor: 'var(--hc-status-info-bg)',
                                color: 'var(--hc-status-info-text)',
                            }}
                        >
                            {displayValue(claimSubmission.purpose)}
                        </span>
                        <h2 className="fw-bold text-white mb-2" style={{ fontSize: 'clamp(1.45rem, 2.2vw, 2rem)' }}>
                            {heroTitle}
                        </h2>
                        <p className="mb-1" style={{ color: 'rgba(var(--hc-white-rgb), 0.78)' }}>
                            Member ID: {displayValue(insured.memberId)}
                        </p>
                        <p className="mb-0" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                            Payer: {displayValue(payer.name?.organization)} · Payer ID: {displayValue(payer.id)}
                        </p>
                    </div>
                    <div className="col-lg-4">
                        <div className="p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.10)' }}>
                            <div className="small mb-1" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                                Patient Control Number
                            </div>
                            <div className="fw-bold text-white fs-5">
                                {displayValue(billing.patientControlNumber)}
                            </div>
                            <div className="small mt-2" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                                Total Charge
                            </div>
                            <div className="fw-bold text-white fs-5">{displayValue(billing.totalCharge)}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12 col-lg-6">
                    <DetailSection title="Claim and Authorization" icon={MdReceiptLong}>
                        <FieldCard label="Purpose" value={claimSubmission.purpose} />
                        <FieldCard label="Assignment Authorization" value={authorization.insuredAuthorizesAssignment} />
                        <FieldCard label="Medical Information Release" value={authorization.patientReleasesMedicalInfo} />
                        <FieldCard label="Provider Assignment" value={authorization.providerAcceptsAssignment} />
                        <FieldCard label="Provider Signature" value={authorization.providerSignature} />
                    </DetailSection>
                </div>
                <div className="col-12 col-lg-6">
                    <DetailSection title="Billing" icon={MdBusiness}>
                        <FieldCard label="Billing Organization" value={billingProvider.name?.organization} />
                        <FieldCard label="Billing NPI" value={billingProvider.identifiers?.npi} />
                        <FieldCard label="Taxonomy Code" value={billingProvider.identifiers?.taxonomyCode} />
                        <FieldCard label="Patient Control Number" value={billing.patientControlNumber} />
                        <FieldCard label="EIN" value={billing.taxId?.ein} />
                        <FieldCard label="Total Charge" value={billing.totalCharge} />
                        <AddressFields address={billingProvider.address} prefix="Billing" />
                    </DetailSection>
                </div>
                <div className="col-12 col-lg-6">
                    <DetailSection title="Insured" icon={MdPerson}>
                        <FieldCard label="First Name" value={insured.name?.person?.firstName} />
                        <FieldCard label="Last Name" value={insured.name?.person?.lastName} />
                        <FieldCard label="Member ID" value={insured.memberId} />
                        <FieldCard label="Date of Birth" value={insured.dateOfBirth} format={formatDate} />
                        <FieldCard label="Gender" value={insured.gender} />
                        <FieldCard label="Insurance Type" value={insured.insuranceType} />
                        <FieldCard label="Responsibility Level" value={insured.paymentResponsibilityLevelCode} />
                        <AddressFields address={insured.address} prefix="Insured" />
                    </DetailSection>
                </div>
                <div className="col-12 col-lg-6">
                    <DetailSection title="Patient and Payer" icon={MdPerson}>
                        <FieldCard label="Patient Name" value={patientName} />
                        <FieldCard label="Patient First Name" value={patient.name?.person?.firstName} />
                        <FieldCard label="Patient Last Name" value={patient.name?.person?.lastName} />
                        <FieldCard label="Patient Date of Birth" value={patient.dateOfBirth} format={formatDate} />
                        <FieldCard label="Relationship to Insured" value={patient.relationshipToInsured} />
                        <AddressFields address={patient.address} prefix="Patient" />
                        <FieldCard label="Payer ID" value={payer.id} />
                        <FieldCard label="Payer Organization" value={payer.name?.organization} />
                    </DetailSection>
                </div>
                <div className="col-12">
                    <DetailSection title="Encounter" icon={MdDescription}>
                        <FieldCard label="Primary Diagnosis Code" value={encounter.primaryDiagnosisCode} />
                        <FieldCard label="Claim Codes" value={claimCodes} />
                        <FieldCard label="Claim Note" value={encounter.claimNote?.additionalInformation} />
                        <FieldCard label="CLIA" value={referenceNumbers.clia} />
                        <FieldCard label="Prior Authorization" value={referenceNumbers.priorAuthorization} />
                        <FieldCard label="Referral" value={referenceNumbers.referral} />
                        <FieldCard label="Initial Treatment" value={clinicalDates.initialTreatment} format={formatDate} />
                        <FieldCard label="Onset of Current Illness" value={clinicalDates.onsetOfCurrentIllness} format={formatDate} />
                        <FieldCard label="Hospitalization Start" value={clinicalDates.hospitalization?.start} format={formatDate} />
                        <FieldCard label="Hospitalization End" value={clinicalDates.hospitalization?.end} format={formatDate} />
                        <FieldCard label="Unable to Work Start" value={clinicalDates.unableToWork?.start} format={formatDate} />
                        <FieldCard label="Unable to Work End" value={clinicalDates.unableToWork?.end} format={formatDate} />
                        <FieldCard label="Auto Accident Related" value={encounter.patientCondition?.isAutoAccidentRelated} />
                        <FieldCard label="Employment Related" value={encounter.patientCondition?.isEmploymentRelated} />
                        <FieldCard label="Other Accident Related" value={encounter.patientCondition?.isOtherAccidentRelated} />
                        <ProviderFields title="Referring Provider" provider={encounter.referringProvider} />
                        <ProviderFields title="Prior Referring Provider" provider={encounter.priorReferringProvider} />
                        <ProviderFields title="Supervising Provider" provider={encounter.supervisingProvider} />
                    </DetailSection>
                </div>
                <div className="col-12 col-lg-6">
                    <DetailSection title="Submitter" icon={MdBusiness}>
                        <FieldCard label="Organization" value={submitter.name?.organization} />
                        <FieldCard label="ETIN" value={submitter.etin} />
                        <FieldCard label="Phone Number" value={submitter.contact?.phoneNumber} />
                    </DetailSection>
                </div>
                {otherInsured.length > 0 && (
                    <div className="col-12 col-lg-6">
                        <DetailSection title={`Other Insured (${otherInsured.length})`} icon={MdPerson}>
                            {otherInsured.map((item, index) => (
                                <React.Fragment key={item.id ?? index}>
                                    <FieldCard label={`Insured ${index + 1} First Name`} value={item.name?.person?.firstName} />
                                    <FieldCard label={`Insured ${index + 1} Last Name`} value={item.name?.person?.lastName} />
                                    <FieldCard label={`Insured ${index + 1} Member ID`} value={item.memberId} />
                                </React.Fragment>
                            ))}
                        </DetailSection>
                    </div>
                )}
                <div className="col-12">
                    <DetailSection title={`Service Lines (${serviceLines.length})`} icon={MdReceiptLong}>
                        {serviceLines.length ? serviceLines.map((line, index) => (
                            <div className="col-12" key={line.id ?? index}>
                                <div className="p-3 rounded-4 border bg-light">
                                    <h6 className="fw-bold text-dark mb-3">Service Line {index + 1}</h6>
                                    <div className="row g-3">
                                        <FieldCard label="Procedure Code" value={line.procedureCode?.code} />
                                        <FieldCard label="Diagnosis Codes" value={line.diagnosisCodes} />
                                        <FieldCard label="Line Charge" value={line.lineItemChargeAmount} />
                                        <FieldCard label="Place of Service" value={line.placeOfService} />
                                        <FieldCard label="Units" value={line.units} />
                                        <FieldCard label="Service Start" value={line.datesOfService?.start} format={formatDate} />
                                        <FieldCard label="Service End" value={line.datesOfService?.end} format={formatDate} />
                                    </div>
                                </div>
                            </div>
                        )) : (
                            <div className="col-12 text-muted">No service lines provided.</div>
                        )}
                    </DetailSection>
                </div>
                {attachments.length > 0 && (
                    <div className="col-12">
                        <DetailSection title={`Attachments (${attachments.length})`} icon={MdDescription}>
                            {attachments.map((attachment, index) => (
                                <React.Fragment key={attachment.attachmentControlNumber || index}>
                                    <FieldCard label={`Attachment ${index + 1} Control Number`} value={attachment.attachmentControlNumber} />
                                    <FieldCard label={`Attachment ${index + 1} Report Type`} value={attachment.reportTypeCode} />
                                    <FieldCard label={`Attachment ${index + 1} Transmission Code`} value={attachment.transmissionCode} />
                                </React.Fragment>
                            ))}
                        </DetailSection>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ClaimSubmissionView;
